import { IProduct } from '@/context/ProductDataStore'
import { IBasketItem } from '@/context/BasketStore'
import { sendShopServerEvent } from '@/services/api'
import { buildEventTrackingContext, readCookieConsentSnapshot } from '@/utils/attribution'

// Custom component type for tracking
interface CustomComponent {
    componentVariantId: { $oid: string }
    componentType: string
    name: string
    position: string | null
    chosenMaterial: { $oid: string }
    display?: Array<{
        configuratorFrontOn: boolean
        'z-index'?: number
    }>
    materialOptions?: string[]
}

// Tracking event types
interface MetaPixelEventData {
    content_ids?: string[]
    content_id?: string
    content_name?: string
    content_type?: 'product' | 'product_group'
    value?: number
    currency?: string
    country?: string
    contents?: TrackingProduct[]
    num_items?: number
    components?: CustomComponent[]
}

interface GAEventData {
    event_category?: string
    event_label?: string
    value?: number
    currency?: string
    items?: GAProduct[]
    country?: string
    analytics_storage?: string
    ad_storage?: string
    anonymize_ip?: boolean
    cookie_flags?: string
}

interface GAProduct {
    item_id: string
    item_name: string
    item_variant?: string
    price: number
    quantity: number
    currency: string
}

declare global {
    interface Window {
        fbq?: (...args: any[]) => void
        gtag?: (command: string, target?: string | Date, parameters?: GAEventData | any) => void
    }
}

interface TrackingProduct {
    id: string
    name: string
    variant?: string
    price: number
    category?: string
    quantity?: number
}

type PurchaseTrackingOptions = {
    transactionId?: string
    currency?: string
    eventId?: string
}

type OrderTrackingItem = {
    productItemId: string
    productName?: string
    optionTitle?: string
    quantity: number
    paymentValue: number
}

const resolveCurrencyCode = (currency?: string): string => (currency || 'GBP').toUpperCase()

const buildEventId = (prefix: string): string => {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
        return `${prefix}_${crypto.randomUUID()}`
    }
    return `${prefix}_${Date.now()}`
}

const canSendServerSideTrackingEvent = (): boolean => {
    const consent = readCookieConsentSnapshot()
    return Boolean(consent.hasConsented && (consent.analytics || consent.marketing))
}

// Helper function to format products for Meta Pixel
const formatProductForPixel = (item: IBasketItem | IProduct, quantity: number = 1): TrackingProduct => {
    console.log('pixel item', item)
    if ('colourWay' in item) {

        // IBasketItem
        const formattedProduct = {
            id: item.colourWay._id,
            name: item.colourWay.productTitle,
            variant: item.colourWay.optionTitle,
            price: item.price,
            quantity
        }
        console.log('[Meta Pixel] Formatting basket item:', formattedProduct)
        return formattedProduct
    } else {
        // IProduct
        const price = item.price?.unit_amount ||
            item.availableOptions?.[0]?.options?.[0]?.price?.unit_amount ||
            0
        const formattedProduct = {
            id: item._id,
            name: item.productTitle,
            variant: item.optionTitle,
            price,
            quantity
        }
        console.log('[Meta Pixel] Formatting product:', formattedProduct)
        return formattedProduct
    }
}

// Helper function to format products for GA4
const formatProductForGA = (item: IBasketItem | IProduct, quantity: number = 1): GAProduct => {
    const currency = resolveCurrencyCode(
        'colourWay' in item
            ? item.colourWay.price?.currency
            : item.price?.currency
    )

    if ('colourWay' in item) {
        // IBasketItem
        const formattedProduct = {
            item_id: item.colourWay._id,
            item_name: item.colourWay.productTitle,
            item_variant: item.colourWay.optionTitle,
            price: item.price,
            quantity,
            currency
        }
        console.log('[Google Analytics] Formatting basket item:', formattedProduct)
        return formattedProduct
    } else {
        // IProduct
        const price = item.price?.unit_amount ||
            item.availableOptions?.[0]?.options?.[0]?.price?.unit_amount ||
            0
        const formattedProduct = {
            item_id: item._id,
            item_name: item.productTitle,
            item_variant: item.optionTitle,
            price,
            quantity,
            currency
        }
        console.log('[Google Analytics] Formatting product:', formattedProduct)
        return formattedProduct
    }
}

const emitJourneyPopupTrigger = (triggerEvent: 'product_view' | 'add_to_cart', region: string | null) => {
    if (typeof window === 'undefined') return
    window.dispatchEvent(new CustomEvent('kibu:journey-popup-trigger', {
        detail: {
            triggerEvent,
            countryCode: region ? region.toUpperCase() : undefined,
        },
    }))
}

// ViewContent - Track when users view product details
export const trackViewContent = (product: IProduct, region: string | null, price?: number, currency?: string) => {
    emitJourneyPopupTrigger('product_view', region)
    const eventCurrency = resolveCurrencyCode(currency || product.price?.currency)
    const formattedProduct = formatProductForPixel(product)
    if (canSendServerSideTrackingEvent()) {
        const consent = readCookieConsentSnapshot()
        const trackingContext = buildEventTrackingContext(consent, buildEventId('view_content'))
        void sendShopServerEvent({
            ...trackingContext,
            eventName: 'ViewContent',
            actionSource: 'website',
            currency: eventCurrency,
            value: price ?? formattedProduct.price,
            contentIds: [formattedProduct.id],
            country: region || undefined,
        })
    }

    // Meta Pixel tracking
    if (!window.fbq) {
        console.warn('[Meta Pixel] ViewContent event not sent - fbq not initialized')
    } else {
        console.log('formattedProduct', formattedProduct)
        const eventData: MetaPixelEventData = {
            content_ids: [formattedProduct.id],
            content_name: formattedProduct.name,
            content_type: 'product',
            value: price ?? formattedProduct.price,
            currency: eventCurrency
        }
        if (region) {
            eventData.country = region
        }
        console.log('[Meta Pixel] Tracking ViewContent:', eventData)
        window.fbq('track', 'ViewContent', eventData)
    }

    // Google Analytics tracking
    if (!window.gtag) {
        console.warn('[Google Analytics] ViewContent event not sent - gtag not initialized')
    } else {
        const formattedProduct = formatProductForGA(product)
        const eventData = {
            currency: eventCurrency,
            value: price ?? formattedProduct.price,
            items: [formattedProduct]
        }
        console.log('[Google Analytics] Tracking view_item:', eventData)
        window.gtag('event', 'view_item', eventData)
    }
}

// AddToCart - Track when items are added to basket
export const trackAddToCart = (item: IBasketItem, region: string | null, currency?: string) => {
    emitJourneyPopupTrigger('add_to_cart', region)
    const eventCurrency = resolveCurrencyCode(currency || item.colourWay.price?.currency)
    const formattedProduct = formatProductForPixel(item)
    const contents = [
        formattedProduct,
        ...item.extras.map(extra => formatProductForPixel(extra))
    ]
    if (canSendServerSideTrackingEvent()) {
        const consent = readCookieConsentSnapshot()
        const trackingContext = buildEventTrackingContext(consent, buildEventId('add_to_cart'))
        void sendShopServerEvent({
            ...trackingContext,
            eventName: 'AddToCart',
            actionSource: 'website',
            orderId: item.id,
            currency: eventCurrency,
            value: item.price,
            numItems: contents.length,
            contentIds: contents.map((productItem) => productItem.id),
            contents: contents.map((productItem) => ({
                id: productItem.id,
                quantity: productItem.quantity || 1,
                item_price: productItem.price,
            })),
            country: region || undefined,
        })
    }

    // Meta Pixel tracking
    if (!window.fbq) {
        console.warn('[Meta Pixel] AddToCart event not sent - fbq not initialized')
    } else {
        const eventData: MetaPixelEventData = {
            content_ids: contents.map(p => p.id),
            content_name: formattedProduct.name,
            content_type: 'product',
            contents,
            value: item.price,
            currency: eventCurrency
        }
        if (region) {
            eventData.country = region
        }
        console.log('[Meta Pixel] Tracking AddToCart:', eventData)
        window.fbq('track', 'AddToCart', eventData)
    }

    // Google Analytics tracking
    if (!window.gtag) {
        console.warn('[Google Analytics] AddToCart event not sent - gtag not initialized')
    } else {
        const formattedProduct = formatProductForGA(item)
        const items = [
            formattedProduct,
            ...item.extras.map(extra => formatProductForGA(extra))
        ]
        const eventData = {
            currency: eventCurrency,
            value: item.price,
            items
        }
        console.log('[Google Analytics] Tracking add_to_cart:', eventData)
        window.gtag('event', 'add_to_cart', eventData)
    }
}

// InitiateCheckout - Track when checkout begins
export const trackInitiateCheckout = (
    items: IBasketItem[],
    totalValue: number,
    region: string | null,
    currency?: string,
    eventId?: string
) => {
    const eventCurrency = resolveCurrencyCode(currency || items[0]?.colourWay?.price?.currency)
    const contents = items.flatMap(item => [
        formatProductForPixel(item),
        ...item.extras.map(extra => formatProductForPixel(extra))
    ])

    // Meta Pixel tracking
    if (!window.fbq) {
        console.warn('[Meta Pixel] InitiateCheckout event not sent - fbq not initialized')
    } else {
        const eventData: MetaPixelEventData = {
            content_ids: contents.map(p => p.id),
            contents,
            num_items: contents.length,
            value: totalValue,
            currency: eventCurrency
        }
        if (region) {
            eventData.country = region
        }
        console.log('[Meta Pixel] Tracking InitiateCheckout:', eventData)
        if (eventId) {
            window.fbq('track', 'InitiateCheckout', eventData, { eventID: eventId })
        } else {
            window.fbq('track', 'InitiateCheckout', eventData)
        }
    }

    // Google Analytics tracking
    if (!window.gtag) {
        console.warn('[Google Analytics] InitiateCheckout event not sent - gtag not initialized')
    } else {
        const gaItems = items.flatMap(item => [
            formatProductForGA(item),
            ...item.extras.map(extra => formatProductForGA(extra))
        ])
        const eventData = {
            currency: eventCurrency,
            value: totalValue,
            items: gaItems
        }
        console.log('[Google Analytics] Tracking begin_checkout:', eventData)
        window.gtag('event', 'begin_checkout', eventData)
    }
}

export const trackServerPageView = (): void => {
    if (!canSendServerSideTrackingEvent()) {
        return
    }
    const consent = readCookieConsentSnapshot()
    const trackingContext = buildEventTrackingContext(consent, buildEventId('page_view'))
    void sendShopServerEvent({
        ...trackingContext,
        eventName: 'PageView',
        actionSource: 'website',
    })
}

// Purchase - Track completed purchases
export const trackPurchase = (
    items: IBasketItem[],
    totalValue: number,
    region: string | null,
    options?: PurchaseTrackingOptions
) => {
    const eventCurrency = resolveCurrencyCode(options?.currency || items[0]?.colourWay?.price?.currency)
    const transactionId = options?.transactionId || Date.now().toString()
    // Meta Pixel tracking
    if (!window.fbq) {
        console.warn('[Meta Pixel] Purchase event not sent - fbq not initialized')
    } else {
        const contents = items.flatMap(item => [
            formatProductForPixel(item),
            ...item.extras.map(extra => formatProductForPixel(extra))
        ])
        const eventData: MetaPixelEventData = {
            content_ids: contents.map(p => p.id),
            contents,
            num_items: contents.length,
            value: totalValue,
            currency: eventCurrency
        }
        if (region) {
            eventData.country = region
        }
        console.log('[Meta Pixel] Tracking Purchase:', eventData)
        if (options?.eventId) {
            window.fbq('track', 'Purchase', eventData, { eventID: options.eventId })
        } else {
            window.fbq('track', 'Purchase', eventData)
        }
    }

    // Google Analytics tracking
    if (!window.gtag) {
        console.warn('[Google Analytics] Purchase event not sent - gtag not initialized')
    } else {
        const gaItems = items.flatMap(item => [
            formatProductForGA(item),
            ...item.extras.map(extra => formatProductForGA(extra))
        ])
        const eventData = {
            transaction_id: transactionId,
            currency: eventCurrency,
            value: totalValue,
            items: gaItems
        }
        console.log('[Google Analytics] Tracking purchase:', eventData)
        window.gtag('event', 'purchase', eventData)
    }
}

// RemoveFromCart - Track when items are removed from basket
export const trackRemoveFromCart = (item: IBasketItem, region: string | null, currency?: string) => {
    const eventCurrency = resolveCurrencyCode(currency || item.colourWay.price?.currency)
    const formattedProduct = formatProductForPixel(item)
    const contents = [
        formattedProduct,
        ...item.extras.map(extra => formatProductForPixel(extra))
    ]
    if (canSendServerSideTrackingEvent()) {
        const consent = readCookieConsentSnapshot()
        const trackingContext = buildEventTrackingContext(consent, buildEventId('remove_from_cart'))
        void sendShopServerEvent({
            ...trackingContext,
            eventName: 'RemoveFromCart',
            actionSource: 'website',
            orderId: item.id,
            currency: eventCurrency,
            value: item.price,
            numItems: contents.length,
            contentIds: contents.map((productItem) => productItem.id),
            contents: contents.map((productItem) => ({
                id: productItem.id,
                quantity: productItem.quantity || 1,
                item_price: productItem.price,
            })),
            country: region || undefined,
        })
    }

    // Meta Pixel tracking
    if (!window.fbq) {
        console.warn('[Meta Pixel] RemoveFromCart event not sent - fbq not initialized')
    } else {
        const eventData: MetaPixelEventData = {
            content_ids: contents.map(p => p.id),
            content_name: formattedProduct.name,
            content_type: 'product',
            contents,
            value: item.price,
            currency: eventCurrency
        }
        if (region) {
            eventData.country = region
        }
        console.log('[Meta Pixel] Tracking RemoveFromCart:', eventData)
        window.fbq('track', 'RemoveFromCart', eventData)
    }

    // Google Analytics tracking
    if (!window.gtag) {
        console.warn('[Google Analytics] RemoveFromCart event not sent - gtag not initialized')
    } else {
        const formattedProduct = formatProductForGA(item)
        const items = [
            formattedProduct,
            ...item.extras.map(extra => formatProductForGA(extra))
        ]
        const eventData = {
            currency: eventCurrency,
            value: item.price,
            items
        }
        console.log('[Google Analytics] Tracking remove_from_cart:', eventData)
        window.gtag('event', 'remove_from_cart', eventData)
    }
}

// Custom event - Track when users start customizing their headphones
export const trackStartCustomization = (baseProduct: IProduct, region: string | null) => {
    const eventCurrency = resolveCurrencyCode(baseProduct.price?.currency)
    // Meta Pixel tracking
    if (!window.fbq) {
        console.warn('[Meta Pixel] StartCustomization event not sent - fbq not initialized')
    } else {
        const formattedProduct = formatProductForPixel(baseProduct)
        const eventData: MetaPixelEventData = {
            content_id: formattedProduct.id,
            content_name: formattedProduct.name,
            value: formattedProduct.price,
            currency: eventCurrency
        }
        if (region) {
            eventData.country = region
        }
        console.log('[Meta Pixel] Tracking StartCustomization:', eventData)
        window.fbq('trackCustom', 'StartCustomization', eventData)
    }

    // Google Analytics tracking
    if (!window.gtag) {
        console.warn('[Google Analytics] StartCustomization event not sent - gtag not initialized')
    } else {
        const formattedProduct = formatProductForGA(baseProduct)
        const eventData = {
            currency: eventCurrency,
            value: formattedProduct.price,
            items: [formattedProduct]
        }
        console.log('[Google Analytics] Tracking start_customization:', eventData)
        window.gtag('event', 'start_customization', eventData)
    }
}

// Custom event - Track when users complete customization
export const trackCompleteCustomization = (customizedProduct: IProduct, selectedComponents: CustomComponent[], region: string | null) => {
    const eventCurrency = resolveCurrencyCode(customizedProduct.price?.currency)
    // Meta Pixel tracking
    if (!window.fbq) {
        console.warn('[Meta Pixel] CompleteCustomization event not sent - fbq not initialized')
    } else {
        const formattedProduct = formatProductForPixel(customizedProduct)
        const eventData: MetaPixelEventData = {
            content_id: formattedProduct.id,
            content_name: formattedProduct.name,
            value: formattedProduct.price,
            components: selectedComponents,
            currency: eventCurrency
        }
        if (region) {
            eventData.country = region
        }
        console.log('[Meta Pixel] Tracking CompleteCustomization:', eventData)
        window.fbq('trackCustom', 'CompleteCustomization', eventData)
    }

    // Google Analytics tracking
    if (!window.gtag) {
        console.warn('[Google Analytics] CompleteCustomization event not sent - gtag not initialized')
    } else {
        const formattedProduct = formatProductForGA(customizedProduct)
        const eventData = {
            currency: eventCurrency,
            value: formattedProduct.price,
            items: [formattedProduct],
            components: selectedComponents
        }
        console.log('[Google Analytics] Tracking complete_customization:', eventData)
        window.gtag('event', 'complete_customization', eventData)
    }
}

// Track payment failures
export const trackPaymentFailed = (items: IBasketItem[], totalValue: number, region: string | null, currency?: string) => {
    const eventCurrency = resolveCurrencyCode(currency || items[0]?.colourWay?.price?.currency)
    // Meta Pixel tracking
    if (!window.fbq) {
        console.warn('[Meta Pixel] PaymentFailed event not sent - fbq not initialized')
    } else {
        const contents = items.flatMap(item => [
            formatProductForPixel(item),
            ...item.extras.map(extra => formatProductForPixel(extra))
        ])
        const eventData: MetaPixelEventData = {
            content_ids: contents.map(p => p.id),
            contents,
            num_items: contents.length,
            value: totalValue,
            currency: eventCurrency
        }
        if (region) {
            eventData.country = region
        }
        console.log('[Meta Pixel] Tracking PaymentFailed:', eventData)
        window.fbq('trackCustom', 'PaymentFailed', eventData)
    }

    // Google Analytics tracking
    if (!window.gtag) {
        console.warn('[Google Analytics] PaymentFailed event not sent - gtag not initialized')
    } else {
        const gaItems = items.flatMap(item => [
            formatProductForGA(item),
            ...item.extras.map(extra => formatProductForGA(extra))
        ])
        const eventData = {
            currency: eventCurrency,
            value: totalValue,
            items: gaItems,
            payment_type: 'stripe'
        }
        console.log('[Google Analytics] Tracking payment_failed:', eventData)
        window.gtag('event', 'payment_failed', eventData)
    }
}

export const trackPurchaseFromOrderData = (
    orderNumber: string,
    items: OrderTrackingItem[],
    totalValueMinor: number,
    currency?: string,
    eventId?: string
) => {
    if (!items.length) {
        return false
    }

    const eventCurrency = resolveCurrencyCode(currency)
    const totalValueMajor = totalValueMinor / 100

    const metaContents: TrackingProduct[] = items.map((item) => ({
        id: item.productItemId,
        name: item.productName || item.optionTitle || item.productItemId,
        variant: item.optionTitle,
        price: item.paymentValue / 100,
        quantity: item.quantity,
    }))

    const gaItems: GAProduct[] = items.map((item) => ({
        item_id: item.productItemId,
        item_name: item.productName || item.optionTitle || item.productItemId,
        item_variant: item.optionTitle,
        price: item.paymentValue / 100,
        quantity: item.quantity,
        currency: eventCurrency,
    }))

    let sentAnyEvent = false

    if (window.fbq) {
        const payload: MetaPixelEventData = {
            content_ids: metaContents.map((item) => item.id),
            contents: metaContents,
            num_items: metaContents.reduce((sum, item) => sum + (item.quantity || 0), 0),
            value: totalValueMajor,
            currency: eventCurrency,
        }
        if (eventId) {
            window.fbq('track', 'Purchase', payload, { eventID: eventId })
        } else {
            window.fbq('track', 'Purchase', payload)
        }
        sentAnyEvent = true
    }

    if (window.gtag) {
        window.gtag('event', 'purchase', {
            transaction_id: orderNumber,
            currency: eventCurrency,
            value: totalValueMajor,
            items: gaItems,
        })
        sentAnyEvent = true
    }

    return sentAnyEvent
}
